import type { ComputedRef } from 'vue'
import type { LinkNavTab } from '@shared/ui/menus'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { buildDisciplineTaskCommentsUrl } from '../lib/discipline-task-comments'

interface DisciplineDetailMenuReturn {
  config: ComputedRef<LinkNavTab[]>
}

/** Same tabs, order and gating as the legacy `DisciplinesMenu`; a tab moves from a main-app URL to
 *  an app route as its page is migrated. */
export const useDisciplineDetailMenu = (): DisciplineDetailMenuReturn => {
  const route = useRoute()
  const disciplineId = route.params.id as string
  const disciplineStore = useDisciplineStore()
  const {
    canViewDisciplines,
    canSimpleViewDisciplines,
    canViewProgressReport,
  } = useDisciplinePrivileges()

  const generalInfoUrl = resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: disciplineId }) ?? ''
  const lessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: disciplineId }) ?? ''
  const balancesUrl = resolveUrl(AppRoute.DisciplinesBalances, { id: disciplineId }) ?? ''
  const tasksUrl = resolveUrl(AppRoute.DisciplinesTasks, { id: disciplineId }) ?? ''
  const filesUrl = resolveUrl(AppRoute.DisciplinesFiles, { id: disciplineId }) ?? ''
  const reportUrl = resolveUrl(AppRoute.DisciplinesReport, { id: disciplineId }) ?? ''
  const stageLogsUrl = resolveUrl(AppRoute.DisciplinesStageLogs, { id: disciplineId }) ?? ''
  const commentsUrl = resolveUrl(AppRoute.DisciplinesComments, { id: disciplineId }) ?? ''
  const changelogUrl = resolveUrl(AppRoute.DisciplinesChangelog, { id: disciplineId }) ?? ''
  const surveysUrl = resolveUrl(AppRoute.DisciplinesSurveys, { id: disciplineId }) ?? ''
  const activationUrl = resolveUrl(AppRoute.DisciplinesActivation, { id: disciplineId }) ?? ''
  const progressReportsUrl = resolveUrl(AppRoute.DisciplinesProgressReports, { id: disciplineId }) ?? ''
  const taskCommentsPath = resolveUrl(AppRoute.DisciplinesTaskComments, { id: disciplineId }) ?? ''
  const taskCommentsUrl = buildDisciplineTaskCommentsUrl(disciplineId) ?? ''

  const config = computed<LinkNavTab[]>(() => {
    const isAnyView = canViewDisciplines.value || canSimpleViewDisciplines.value
    const isFullView = canViewDisciplines.value

    return [
      {
        text: 'General info',
        to: generalInfoUrl,
        selected: route.path === generalInfoUrl,
        disabled: false,
        visible: isAnyView,
      },
      {
        text: 'Lessons',
        to: lessonsUrl,
        selected: route.path === lessonsUrl,
        disabled: false,
        visible: isAnyView,
      },
      {
        text: 'Balance',
        to: balancesUrl,
        selected: route.path === balancesUrl,
        disabled: false,
        visible: isAnyView,
      },
      {
        text: 'Tasks',
        to: tasksUrl,
        selected: route.path === tasksUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Files',
        to: filesUrl,
        selected: route.path === filesUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Demo lesson report',
        to: reportUrl,
        selected: route.path === reportUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Kanban log',
        to: stageLogsUrl,
        selected: route.path === stageLogsUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Comments',
        to: commentsUrl,
        selected: route.path === commentsUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Task comments',
        to: taskCommentsUrl,
        selected: route.path === taskCommentsPath,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Changelog',
        to: changelogUrl,
        selected: route.path === changelogUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Survey',
        to: surveysUrl,
        selected: route.path === surveysUrl,
        disabled: false,
        visible: isFullView,
      },
      {
        text: 'Activation',
        to: activationUrl,
        selected: route.path === activationUrl,
        disabled: !disciplineStore.getDiscipline?.stage,
        visible: isFullView,
      },
      {
        text: 'Progress reports',
        to: progressReportsUrl,
        selected: route.path === progressReportsUrl,
        disabled: false,
        visible: canViewProgressReport.value,
      },
    ]
  })

  return {
    config,
  }
}
